package ca.northroot.app.ui.jobs

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.*
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ca.northroot.app.data.*
import ca.northroot.app.ui.components.*
import ca.northroot.app.ui.theme.*
import ca.northroot.app.utils.DateTimeUtils
import ca.northroot.app.utils.PlatformUtils
import kotlinx.datetime.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun JobsScreen(
    db: Db,
    userProfile: UserProfile?,
    isAdmin: Boolean,
    onBack: () -> Unit,
    companyId: String,
    onLogout: () -> Unit
) {
    var allJobs by remember { mutableStateOf<List<Job>>(emptyList()) }
    var customers by remember { mutableStateOf<Map<String, Customer>>(emptyMap()) }
    var isLoading by remember { mutableStateOf(false) }
    
    // Day Selection State
    var selectedDate by remember { 
        mutableStateOf(Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date) 
    }

    LaunchedEffect(companyId) {
        if (companyId.isBlank()) return@LaunchedEffect
        isLoading = true
        db.getJobs(companyId, 0).collect { 
            allJobs = it 
            isLoading = false
        }
    }

    LaunchedEffect(companyId) {
        if (companyId.isBlank()) return@LaunchedEffect
        db.getCustomers(companyId).collect { list ->
            customers = list.associateBy { it.id }
        }
    }

    val filteredJobs = remember(allJobs, selectedDate) {
        allJobs.filter { job ->
            val jobTime = job.scheduledDate ?: 0L
            if (jobTime == 0L) return@filter false
            
            val jobDate = Instant.fromEpochMilliseconds(jobTime)
                .toLocalDateTime(TimeZone.currentSystemDefault()).date
            
            jobDate == selectedDate
        }
    }

    Scaffold(
        topBar = {
            Column {
                TopAppBar(
                    title = { 
                        Column {
                            Text("Daily Schedule", fontWeight = FontWeight.Black)
                            Text(
                                "${selectedDate.dayOfWeek.name}, ${selectedDate.month.name} ${selectedDate.dayOfMonth}", 
                                style = MaterialTheme.typography.labelSmall, 
                                color = NorthrootGold
                            )
                        }
                    },
                    navigationIcon = { IconButton(onClick = onBack) { Icon(Icons.AutoMirrored.Filled.ArrowBack, null) } },
                    actions = {
                        IconButton(onClick = {
                            selectedDate = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
                        }) { Icon(Icons.Default.Today, "Today") }
                        IconButton(onClick = onLogout) { Icon(Icons.AutoMirrored.Filled.Logout, null) }
                    },
                    colors = TopAppBarDefaults.topAppBarColors(
                        containerColor = MaterialTheme.colorScheme.surface,
                        titleContentColor = MaterialTheme.colorScheme.onSurface,
                        navigationIconContentColor = MaterialTheme.colorScheme.primary,
                        actionIconContentColor = MaterialTheme.colorScheme.primary
                    )
                )
                
                WeekDatePicker(
                    selectedDate = selectedDate,
                    onDateSelected = { selectedDate = it }
                )
            }
        },
        floatingActionButton = {
            if (isAdmin) {
                FloatingActionButton(
                    onClick = { }, 
                    containerColor = MaterialTheme.colorScheme.primary,
                    shape = CircleShape
                ) {
                    Icon(Icons.Default.Add, null, tint = MaterialTheme.colorScheme.onPrimary)
                }
            }
        }
    ) { padding ->
        Box(Modifier.padding(padding).fillMaxSize().background(MaterialTheme.colorScheme.background)) {
            if (isLoading && allJobs.isEmpty()) {
                Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                }
            } else if (filteredJobs.isEmpty()) {
                Column(
                    modifier = Modifier.fillMaxSize(),
                    verticalArrangement = Arrangement.Center,
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Icon(Icons.Default.EventBusy, null, modifier = Modifier.size(64.dp), tint = NorthrootGray.copy(alpha = 0.5f))
                    Spacer(Modifier.height(16.dp))
                    Text("No jobs scheduled for this day", color = NorthrootGray)
                    
                    if (companyId.isBlank()) {
                        Text("Error: No Company ID linked to profile.", color = Color.Red, fontSize = 12.sp)
                    } else if (allJobs.isNotEmpty()) {
                        Text(
                            "Total ${allJobs.size} jobs loaded in background.", 
                            style = MaterialTheme.typography.labelSmall,
                            modifier = Modifier.padding(top = 8.dp)
                        )
                        Spacer(Modifier.height(16.dp))
                        Button(onClick = {
                            // Find the first available job date and switch to it
                            val firstJob = allJobs.firstOrNull()
                            if (firstJob?.scheduledDate != null) {
                                selectedDate = Instant.fromEpochMilliseconds(firstJob.scheduledDate!!)
                                    .toLocalDateTime(TimeZone.currentSystemDefault()).date
                            }
                        }) {
                            Text("Jump to Next Scheduled Job")
                        }
                    }
                }
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxSize(), 
                    contentPadding = PaddingValues(16.dp), 
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    items(filteredJobs) { job ->
                        val customer = customers[job.customerId]
                        JobItem(
                            job = job, 
                            address = customer?.address ?: "Address lookup failed...",
                            onClick = {}
                        )
                    }
                    item { Spacer(Modifier.height(80.dp)) }
                }
            }
        }
    }
}

@Composable
fun JobItem(job: Job, address: String, onClick: () -> Unit) {
    NorthrootCard(onClick = onClick) {
        Column(Modifier.padding(20.dp)) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Top) {
                Column(Modifier.weight(1f)) {
                    Text(
                        job.customerName, 
                        style = MaterialTheme.typography.titleLarge, 
                        fontWeight = FontWeight.ExtraBold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    Spacer(Modifier.height(4.dp))
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.LocationOn, null, modifier = Modifier.size(14.dp), tint = NorthrootGray)
                        Spacer(Modifier.width(4.dp))
                        Text(
                            address, 
                            style = MaterialTheme.typography.bodySmall, 
                            color = NorthrootGray,
                            maxLines = 1
                        )
                    }
                }
                NorthrootBadge(job.status)
            }
            
            Spacer(Modifier.height(20.dp))
            
            // Detail Row
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(12.dp))
                    .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f))
                    .padding(12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.Build, null, Modifier.size(16.dp), tint = MaterialTheme.colorScheme.primary)
                    Spacer(Modifier.width(8.dp))
                    Text(job.serviceType, style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold)
                }
                
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Default.AccessTime, null, Modifier.size(16.dp), tint = MaterialTheme.colorScheme.secondary)
                    Spacer(Modifier.width(8.dp))
                    Text(
                        DateTimeUtils.formatTime(job.scheduledDate), 
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = FontWeight.ExtraBold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                }
            }
            
            if (job.status != JobStatus.COMPLETED) {
                Spacer(Modifier.height(20.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                    TonalActionButton(
                        icon = Icons.Default.Directions,
                        label = "NAVIGATE",
                        onClick = { PlatformUtils.launchMap(address) },
                        modifier = Modifier.weight(1f)
                    )
                    
                    TonalActionButton(
                        icon = Icons.Default.Sms,
                        label = "MESSAGE",
                        onClick = { PlatformUtils.launchSms("Client", "Northroot is on the way!") },
                        modifier = Modifier.weight(1f),
                        containerColor = NorthrootGold.copy(alpha = 0.15f),
                        contentColor = NorthrootGold
                    )
                }
            }
        }
    }
}

@Composable
fun TonalActionButton(
    icon: androidx.compose.ui.graphics.vector.ImageVector,
    label: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    containerColor: Color = MaterialTheme.colorScheme.primary.copy(alpha = 0.1f),
    contentColor: Color = MaterialTheme.colorScheme.primary
) {
    Surface(
        onClick = onClick,
        modifier = modifier.height(44.dp),
        shape = RoundedCornerShape(12.dp),
        color = containerColor,
        contentColor = contentColor
    ) {
        Row(
            modifier = Modifier.fillMaxSize(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center
        ) {
            Icon(icon, null, modifier = Modifier.size(18.dp))
            Spacer(Modifier.width(8.dp))
            Text(label, fontSize = 11.sp, fontWeight = FontWeight.Black, letterSpacing = 0.5.sp)
        }
    }
}
